import { useCallback, useRef, useState } from 'react';

import { arrayMove } from '@dnd-kit/helpers';

import type { MarketAppWithTabs } from '@/features/dashboard/types';
import { generateUniqueUUID } from '@/shared/lib/format/generateUniqueUUID';

import { useDashboardEditActions } from '../../../hooks/useDashboardEditActions';
import { BLANK_COLUMN_ID } from '../types';

import type { DragNode } from '../types';

// ============================================================================
// Types
// ============================================================================

type Actions = ReturnType<typeof useDashboardEditActions>;
type MutableRef<T> = { current: T };

interface UseDragDropHandlersParams {
  currentTabId: string | null;
  marketTabApps: MarketAppWithTabs[];
}

interface UseDragDropHandlersResult {
  isDragging: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dnd-kit handler event boundary.
  handleDragStart: (event: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dnd-kit handler event boundary.
  handleDragEnd: (event: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dnd-kit handler event boundary.
  handleDragOver: (event: any) => void;
}

interface CrossAppTarget {
  appId: string;
  insertAtIndex?: number;
}

interface DropHandlerContext {
  currentTabId: string;
  apps: MarketAppWithTabs[];
  actions: Actions;
}

interface PreviewHandlerContext extends DropHandlerContext {
  crossAppTargetRef: MutableRef<CrossAppTarget | null>;
}

// ============================================================================
// Lookup Utilities
// ============================================================================

const findAppByTabId = (apps: MarketAppWithTabs[], tabId: string) =>
  apps.find(app => app.tabs.some(t => t.id === tabId));

const findTabIndex = (app: MarketAppWithTabs, tabId: string) =>
  app.tabs.findIndex(t => t.id === tabId);

const isItemType = (type: string | undefined) => type === 'item' || type === 'singleItem';

const isBlankColumnTarget = (target: DragNode | undefined) =>
  target?.id === BLANK_COLUMN_ID || target?.group === BLANK_COLUMN_ID;

// ============================================================================
// Drop Handlers (pure functions, called from handleDragEnd)
// ============================================================================

/**
 * Tab → Blank Column: removes tab from source app and creates a new app with it.
 * Returns true if handled.
 */
const handleDropOnBlankColumn = (
  source: DragNode,
  currentTabId: string,
  apps: MarketAppWithTabs[],
  actions: Actions
): boolean => {
  const itemId = source.id as string;
  const sourceApp = findAppByTabId(apps, itemId);
  if (!sourceApp || sourceApp.tabs.length === 1) return false;

  const tab = sourceApp.tabs.find(t => t.id === itemId);
  if (!tab) return false;

  const newAppId = generateUniqueUUID(new Set(apps.map(a => a.id)));
  actions.removeTabFromApp(currentTabId, sourceApp.id, itemId);
  actions.addApp(currentTabId, { id: newAppId, tabs: [tab] });
  return true;
};

/**
 * Tab → Another App's empty drop zone: moves tab into target app (appended).
 * Returns true if handled.
 */
const handleDropOnColumnDropZone = (params: {
  source: DragNode;
  target: DragNode;
  context: DropHandlerContext;
  crossAppTarget: CrossAppTarget | null;
}): boolean => {
  const {
    source,
    target,
    context: { currentTabId, apps, actions },
    crossAppTarget
  } = params;

  if (target.type !== 'column-item-drop') return false;

  const itemId = source.id as string;
  const targetAppId = target.data?.columnId as string;
  const sourceApp = findAppByTabId(apps, itemId);

  if (sourceApp && targetAppId && sourceApp.id !== targetAppId) {
    actions.moveTabBetweenApps(
      currentTabId,
      itemId,
      sourceApp.id,
      targetAppId,
      crossAppTarget?.appId === targetAppId ? crossAppTarget.insertAtIndex : undefined
    );
    return true;
  }
  return false;
};

/**
 * Tab → Tab in a different app: moves tab to target app at the hovered position.
 * Returns true if handled.
 */
const handleDropOnCrossAppItem = (params: {
  source: DragNode;
  target: DragNode;
  context: DropHandlerContext;
}): boolean => {
  const {
    source,
    target,
    context: { currentTabId, apps, actions }
  } = params;

  if (!isItemType(target.type)) return false;

  const itemId = source.id as string;
  const targetId = target.id as string;
  const sourceApp = findAppByTabId(apps, itemId);
  const targetApp = findAppByTabId(apps, targetId);

  if (sourceApp && targetApp && sourceApp.id !== targetApp.id) {
    const targetIndex = findTabIndex(targetApp, targetId);
    actions.moveTabBetweenApps(
      currentTabId,
      itemId,
      sourceApp.id,
      targetApp.id,
      targetIndex !== -1 ? targetIndex : undefined
    );
    return true;
  }
  return false;
};

/**
 * Tab ↔ Tab in the same app: reorders tabs within an app.
 * Uses live `marketTabApps` (not snapshot) because same-app reorder
 * needs the current positions after any dragOver updates.
 *
 * When `target` is null or not an item in the same app (can happen when
 * dnd-kit's OptimisticSortingPlugin leaves target as null at dragEnd),
 * falls back to `source.initialIndex` → `source.index` (the projected
 * sortable index kept up-to-date by OptimisticSortingPlugin during drag).
 */
const handleSameAppTabReorder = (params: {
  source: DragNode;
  target: DragNode | null | undefined;
  currentTabId: string;
  liveApps: MarketAppWithTabs[];
  actions: Actions;
}): void => {
  const { source, target, currentTabId, liveApps, actions } = params;
  if (!isItemType(source.type)) return;
  // If target is an item in a different app, this is a cross-app drag — skip.
  if (target && isItemType(target.type) && source.group !== target.group) return;

  const appId = source.group as string;
  let fromIndex: number;
  let toIndex: number;

  if (
    target &&
    isItemType(target.type) &&
    source.group === target.group &&
    target.id !== source.id
  ) {
    // Normal path: target is a different item in the same app.
    const app = liveApps.find(a => a.id === appId);
    if (!app) return;
    fromIndex = findTabIndex(app, source.id as string);
    toIndex = findTabIndex(app, target.id as string);
  } else if (typeof source.index === 'number' && typeof source.initialIndex === 'number') {
    // Fallback: target is null, is source itself (OptimisticSortingPlugin sets
    // dropTarget=source after reordering DOM), or has unexpected type.
    // Use projected sortable indices which are reliably maintained.
    fromIndex = source.initialIndex;
    toIndex = source.index;
  } else {
    return;
  }

  if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
    actions.reorderTabsInsideApp(currentTabId, appId, fromIndex, toIndex);
  }
};

/**
 * App ↔ App: reorders apps in the tab layout.
 *
 * When `target` is null or not a column (can happen when dnd-kit's
 * OptimisticSortingPlugin leaves target as null at dragEnd), falls back
 * to `source.initialIndex` → `source.index` (the projected sortable index
 * kept up-to-date by OptimisticSortingPlugin during drag).
 */
const handleAppReorder = (
  source: DragNode,
  target: DragNode | null | undefined,
  apps: MarketAppWithTabs[],
  actions: Actions
): void => {
  if (source.type !== 'column') return;
  if (target?.id === BLANK_COLUMN_ID) return;

  let fromIndex: number;
  let toIndex: number;

  if (target?.type === 'column' && target.id !== source.id) {
    // Normal path: target is a different column.
    fromIndex = apps.findIndex(a => a.id === source.id);
    toIndex = apps.findIndex(a => a.id === target.id);
  } else if (typeof source.index === 'number' && typeof source.initialIndex === 'number') {
    // Fallback: target is null, is source itself (OptimisticSortingPlugin sets
    // dropTarget=source after reordering DOM), or has unexpected type.
    // Use projected sortable indices which are reliably maintained.
    fromIndex = source.initialIndex;
    toIndex = source.index;
  } else {
    return;
  }

  if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
    actions.updateCurrentTabApps(arrayMove(apps, fromIndex, toIndex));
  }
};

// ============================================================================
// DragOver Preview Handlers (pure functions, called from handleDragOver)
// ============================================================================

/**
 * Live cross-app preview: when dragging a tab over another app's tab,
 * moves the tab in Redux state so the UI shows it shifting into position.
 */
const handleCrossAppItemPreview = (params: {
  source: DragNode;
  target: DragNode;
  context: PreviewHandlerContext;
}): void => {
  const {
    source,
    target,
    context: { currentTabId, apps: snapshot, crossAppTargetRef, actions }
  } = params;

  if (!isItemType(target.type) || source.id === target.id) return;

  const itemId = source.id as string;
  const targetId = target.id as string;
  const sourceApp = findAppByTabId(snapshot, itemId);
  const targetApp = findAppByTabId(snapshot, targetId);

  if (sourceApp && targetApp && sourceApp.id !== targetApp.id) {
    const targetIndex = findTabIndex(targetApp, targetId);
    const newTarget: CrossAppTarget = {
      appId: targetApp.id,
      insertAtIndex: targetIndex !== -1 ? targetIndex : undefined
    };

    const prev = crossAppTargetRef.current;
    if (prev?.appId !== newTarget.appId || prev?.insertAtIndex !== newTarget.insertAtIndex) {
      crossAppTargetRef.current = newTarget;
      actions.moveTabBetweenApps(
        currentTabId,
        itemId,
        sourceApp.id,
        targetApp.id,
        newTarget.insertAtIndex,
        true
      );
    }
  } else if (sourceApp && sourceApp.id === targetApp?.id && crossAppTargetRef.current) {
    // Dragged back to original app — restore snapshot
    crossAppTargetRef.current = null;
    actions.updateCurrentTabApps(snapshot);
  }
};

/**
 * Live preview for column-item-drop zones: when dragging a tab over
 * an app's empty drop zone, moves the tab to that app in Redux state.
 */
const handleColumnDropZonePreview = (params: {
  source: DragNode;
  target: DragNode;
  context: PreviewHandlerContext;
}): void => {
  const {
    source,
    target,
    context: { currentTabId, apps: snapshot, crossAppTargetRef, actions }
  } = params;

  if (target.type !== 'column-item-drop') return;

  const targetAppId = target.data?.columnId as string;
  if (!targetAppId) return;

  const sourceApp = findAppByTabId(snapshot, source.id as string);
  if (!sourceApp || sourceApp.id === targetAppId) return;

  if (crossAppTargetRef.current?.appId !== targetAppId) {
    crossAppTargetRef.current = { appId: targetAppId };
    actions.moveTabBetweenApps(
      currentTabId,
      source.id as string,
      sourceApp.id,
      targetAppId,
      undefined,
      true
    );
  }
};

const handleItemDragEnd = ({
  source,
  target,
  currentTabId,
  snapshot,
  marketTabApps,
  actions,
  crossAppTargetRef
}: {
  source: DragNode;
  target: DragNode | null | undefined;
  currentTabId: string;
  snapshot: MarketAppWithTabs[];
  marketTabApps: MarketAppWithTabs[];
  actions: Actions;
  crossAppTargetRef: MutableRef<CrossAppTarget | null>;
}): void => {
  const context: DropHandlerContext = { currentTabId, apps: snapshot, actions };
  const crossApp = crossAppTargetRef.current;

  if (!crossApp) {
    // No cross-app drag occurred — try drop handlers, then same-app reorder.
    if (target) {
      if (!isBlankColumnTarget(target)) {
        handleDropOnColumnDropZone({ source, target, context, crossAppTarget: null });
        handleDropOnCrossAppItem({ source, target, context });
      } else {
        handleDropOnBlankColumn(source, currentTabId, snapshot, actions);
      }
    }
    // Call unconditionally; handleSameAppTabReorder uses index fallback if target is null.
    handleSameAppTabReorder({
      source,
      target,
      currentTabId,
      liveApps: marketTabApps,
      actions
    });
    return;
  }

  if (target && isBlankColumnTarget(target)) {
    // Cross-app drag ended on blank column — restore snapshot, then handle drop.
    actions.updateCurrentTabApps(snapshot);
    handleDropOnBlankColumn(source, currentTabId, snapshot, actions);
  }
};

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Encapsulates all drag-drop event handlers for AppsOrder.
 *
 * Handles:
 * - Tab → Blank Column: Creates new app with dropped tab
 * - Tab → Other App's Drop Zone: Moves tab between apps (at position)
 * - App ↔ App: Reorders apps
 * - Tab ↔ Tab (same app): Reorders tabs within app
 *
 * ## Snapshot vs Live State
 *
 * - `snapshot` (saved at dragStart): The authoritative state for cross-app
 *   operations and app reorder. Prevents compounding moves during dragOver.
 * - `marketTabApps` (live Redux state): Used only for same-app tab reorder,
 *   which needs current positions after any dragOver updates.
 *
 * Cross-group drags use `preventDefault()` to stop dnd-kit DOM manipulation,
 * and instead update Redux state directly in `onDragOver` for visual feedback.
 * See: https://github.com/clauderic/dnd-kit/issues/1747
 */
export const useDragDropHandlers = ({
  currentTabId,
  marketTabApps
}: UseDragDropHandlersParams): UseDragDropHandlersResult => {
  const actions = useDashboardEditActions();
  const [isDragging, setIsDragging] = useState(false);
  const crossAppTargetRef = useRef<CrossAppTarget | null>(null);
  const snapshotRef = useRef<MarketAppWithTabs[] | null>(null);

  const resetDragState = useCallback(() => {
    snapshotRef.current = null;
    crossAppTargetRef.current = null;
    setIsDragging(false);
  }, []);

  const handleDragStart = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dnd-kit event type varies by handler.
    (_event: any) => {
      setIsDragging(true);
      crossAppTargetRef.current = null;
      snapshotRef.current = marketTabApps.map(app => ({
        ...app,
        tabs: [...app.tabs]
      }));
    },
    [marketTabApps]
  );

  const handleDragEnd = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dnd-kit event type varies by handler.
    (event: any) => {
      const { source, target } = event.operation;
      const snapshot = snapshotRef.current;

      if (!currentTabId || !snapshot || !source) {
        resetDragState();
        return;
      }

      if (event.canceled) {
        actions.updateCurrentTabApps(snapshot);
        resetDragState();
        return;
      }

      // App reorder: call unconditionally; handleAppReorder uses index fallback if target is null.
      if (source.type === 'column') {
        handleAppReorder(source, target, snapshot, actions);
      }

      if (isItemType(source.type)) {
        handleItemDragEnd({
          source,
          target,
          currentTabId,
          snapshot,
          marketTabApps,
          actions,
          crossAppTargetRef
        });
      }

      actions.removeEmptyApps();
      resetDragState();
    },
    [currentTabId, marketTabApps, actions, resetDragState]
  );

  const handleDragOver = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dnd-kit event type varies by handler.
    (event: any) => {
      const { source, target } = event.operation;
      if (!currentTabId || !source || target?.id === undefined) return;

      // Prevent dnd-kit DOM manipulation for cross-group drags
      if (source.group !== target?.group) {
        event.preventDefault();
      }

      if (!isItemType(source.type) || !target) return;

      const snapshot = snapshotRef.current;
      if (!snapshot) return;

      const context: PreviewHandlerContext = {
        currentTabId,
        apps: snapshot,
        crossAppTargetRef,
        actions
      };

      handleCrossAppItemPreview({ source, target, context });
      handleColumnDropZonePreview({ source, target, context });
    },
    [currentTabId, actions]
  );

  return {
    isDragging,
    handleDragStart,
    handleDragEnd,
    handleDragOver
  };
};
